import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import LanguageSwitcher from '../components/LanguageSwitcher'
import DarkModeToggle from '../components/DarkModeToggle'
import SEO from '../components/SEO'

function Spielanleitung() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Generate HowTo Schema.org structured data
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t('spielanleitung.title'),
    description: t('spielanleitung.intro'),
    image: `${window.location.origin}/og-image.png`,
    step: [
      // Moderator Steps
      {
        '@type': 'HowToSection',
        name: t('spielanleitung.forModerators'),
        itemListElement: [
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.step1Title'),
            text: t('spielanleitung.step1Text'),
            position: 1,
          },
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.step2Title'),
            text: t('spielanleitung.step2Text'),
            position: 2,
          },
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.step3Title'),
            text: t('spielanleitung.step3Text'),
            position: 3,
          },
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.step4Title'),
            text: t('spielanleitung.step4Text'),
            position: 4,
          },
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.step5Title'),
            text: t('spielanleitung.step5Text'),
            position: 5,
          },
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.step6Title'),
            text: t('spielanleitung.step6Text'),
            position: 6,
          },
        ],
      },
      // Player Steps
      {
        '@type': 'HowToSection',
        name: t('spielanleitung.forPlayers'),
        itemListElement: [
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.playerStep1Title'),
            text: t('spielanleitung.playerStep1Text'),
            position: 1,
          },
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.playerStep2Title'),
            text: t('spielanleitung.playerStep2Text'),
            position: 2,
          },
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.playerStep3Title'),
            text: t('spielanleitung.playerStep3Text'),
            position: 3,
          },
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.playerStep4Title'),
            text: t('spielanleitung.playerStep4Text'),
            position: 4,
          },
          {
            '@type': 'HowToStep',
            name: t('spielanleitung.playerStep5Title'),
            text: t('spielanleitung.playerStep5Text'),
            position: 5,
          },
        ],
      },
    ],
  }

  return (
    <>
      <SEO page="spielanleitung" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>
      <div className="min-h-screen flex flex-col">
        {/* Controls - Top Right */}
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
          <DarkModeToggle />
          <LanguageSwitcher />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-start justify-center p-4 pt-20">
          <div className="w-full max-w-4xl">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12 transition-colors">
              {/* Back Button */}
              <button
                onClick={() => navigate('/')}
                className="mb-6 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold flex items-center gap-2"
              >
                ← {t('common.back')}
              </button>

              {/* Header */}
              <div className="mb-8">
                <h1 className="text-4xl font-bold mb-4 dark:text-white">
                  {t('spielanleitung.title')}
                </h1>
                <p className="text-xl text-indigo-600 dark:text-indigo-400 font-semibold mb-4">
                  {t('spielanleitung.subtitle')}
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  {t('spielanleitung.intro')}
                </p>
              </div>

              {/* Content */}
              <div className="prose dark:prose-invert max-w-none space-y-8">
                {/* Für Moderatoren */}
                <section>
                  <h2 className="text-3xl font-bold mb-4 text-indigo-600 dark:text-indigo-400 flex items-center gap-3">
                    <span className="text-4xl">👑</span>
                    {t('spielanleitung.forModerators')}
                  </h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">
                    {t('spielanleitung.moderatorIntro')}
                  </p>

                  <div className="space-y-6">
                    {[1, 2, 3, 4, 5, 6].map((step) => (
                      <div
                        key={step}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border-l-4 border-indigo-600"
                      >
                        <h3 className="text-xl font-bold mb-2 dark:text-white">
                          {t(`spielanleitung.step${step}Title`)}
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300">
                          {t(`spielanleitung.step${step}Text`)}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Für Spieler */}
                <section>
                  <h2 className="text-3xl font-bold mb-4 text-cyan-600 dark:text-cyan-400 flex items-center gap-3">
                    <span className="text-4xl">🎮</span>
                    {t('spielanleitung.forPlayers')}
                  </h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">
                    {t('spielanleitung.playerIntro')}
                  </p>

                  <div className="space-y-6">
                    {[1, 2, 3, 4, 5].map((step) => (
                      <div
                        key={step}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border-l-4 border-cyan-600"
                      >
                        <h3 className="text-xl font-bold mb-2 dark:text-white">
                          {t(`spielanleitung.playerStep${step}Title`)}
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300">
                          {t(`spielanleitung.playerStep${step}Text`)}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Karten-Werte */}
                <section>
                  <h2 className="text-3xl font-bold mb-4 text-purple-600 dark:text-purple-400 flex items-center gap-3">
                    <span className="text-4xl">🃏</span>
                    {t('spielanleitung.cardValues')}
                  </h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">
                    {t('spielanleitung.cardValuesText')}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <div
                        key={value}
                        className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900 dark:to-pink-900 rounded-lg p-4"
                      >
                        <p className="text-gray-800 dark:text-gray-200 font-medium">
                          {t(`spielanleitung.cardValue${value}`)}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Tipps */}
                <section>
                  <h2 className="text-3xl font-bold mb-4 text-green-600 dark:text-green-400 flex items-center gap-3">
                    <span className="text-4xl">💡</span>
                    {t('spielanleitung.tips')}
                  </h2>

                  <ul className="space-y-3">
                    {[1, 2, 3, 4, 5, 6].map((tip) => (
                      <li
                        key={tip}
                        className="flex items-start gap-3 text-gray-700 dark:text-gray-300"
                      >
                        <span className="text-green-600 dark:text-green-400 font-bold text-xl mt-1">
                          ✓
                        </span>
                        <span>{t(`spielanleitung.tip${tip}`)}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                {/* Features */}
                <section>
                  <h2 className="text-3xl font-bold mb-4 text-blue-600 dark:text-blue-400 flex items-center gap-3">
                    <span className="text-4xl">⚡</span>
                    {t('spielanleitung.features')}
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[1, 2, 3, 4, 5].map((feature) => (
                      <div
                        key={feature}
                        className="bg-blue-50 dark:bg-blue-900 rounded-lg p-6"
                      >
                        <h3 className="text-lg font-bold mb-2 text-blue-900 dark:text-blue-100">
                          {t(`spielanleitung.feature${feature}Title`)}
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300">
                          {t(`spielanleitung.feature${feature}Text`)}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Call to Action */}
                <section className="text-center pt-8">
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white">
                    <h2 className="text-2xl font-bold mb-4">
                      {t('home.title')}
                    </h2>
                    <p className="mb-6 text-indigo-100">
                      {t('home.subtitle')}
                    </p>
                    <button
                      onClick={() => navigate('/')}
                      className="bg-white text-indigo-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-all transform hover:scale-105 shadow-lg"
                    >
                      {t('home.startSession')}
                    </button>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Spielanleitung
